import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "@/components/base/SEOHead";
import { homeAdvantages, membershipTiers, homePromotions } from "@/mocks/homeData";

export default function Home() {
  const { t } = useTranslation();

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "必赢国际",
    url: "https://www.biyingguoji.com",
    description: "必赢国际是备受信赖的国际化综合平台，提供安全、公正、多元的优质服务体验",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+65-6789-0000",
      contactType: "customer service",
      availableLanguage: ["Chinese", "English"],
    },
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "必赢国际",
    url: "https://www.biyingguoji.com",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.biyingguoji.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <>
      <SEOHead
        title="必赢国际·官方平台入口 - 备受信赖的国际化综合平台"
        description="必赢国际是备受信赖的国际化综合平台，提供安全、公正、多元的优质服务体验。探索必赢国际的核心优势、会员权益与专属福利，立即注册开启您的精彩旅程，感受顶级国际平台带来的全方位保障与无限可能。"
        keywords="必赢国际,必赢国际官网,必赢国际注册,必赢国际会员,必赢国际平台,必赢国际入口,必赢国际优惠活动"
        canonicalUrl="https://www.biyingguoji.com/"
        ogTitle="必赢国际·官方平台入口 - 备受信赖的国际化综合平台"
        ogDescription="必赢国际是备受信赖的国际化综合平台，提供安全、公正、多元的优质服务体验。"
        structuredData={{ ...organizationSchema }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20luxurious%20geometric%20composition%20with%20warm%20golden%20and%20deep%20crimson%20gradients%2C%20modern%20premium%20aesthetic%2C%20smooth%20flowing%20shapes%2C%20dark%20elegant%20background%20with%20subtle%20particle%20effects%2C%20corporate%20premium%20feel%2C%20no%20text%20no%20people%2C%20cinematic%20lighting&width=1800&height=900&seq=hero-bg-main&orientation=landscape"
          alt="必赢国际官方平台入口 - 国际化综合平台"
          title="必赢国际官方平台入口"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/20 mb-6">
              <div className="w-2 h-2 rounded-full bg-accent-400"></div>
              <span className="text-sm text-background-50/90 font-medium">官方认证平台 · 全球信赖之选</span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
              {t("hero.title")}
            </h1>
            <p className="text-lg md:text-xl text-background-50/80 mb-3 font-heading">
              {t("hero.subtitle")}
            </p>
            <p className="text-sm md:text-base text-background-50/65 max-w-2xl mx-auto mb-8">
              {t("hero.description")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/about"
                className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-lg text-base font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                {t("hero.cta_register")}
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/category/platform"
                className="px-8 py-3.5 bg-background-50/10 backdrop-blur-sm text-background-50 border border-background-50/25 rounded-lg text-base font-medium hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t("hero.cta_learn")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="w-full bg-background-100 border-b border-background-200/70">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "1000万+", label: "全球注册用户" },
              { value: "30+", label: "覆盖国家和地区" },
              { value: "99.9%", label: "服务可用率" },
              { value: "7×24", label: "全天候客服支持" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="font-heading text-2xl md:text-3xl font-bold text-primary-600">
                  {stat.value}
                </div>
                <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="w-full py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
              {t("advantages.title")}
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              {t("advantages.subtitle")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {homeAdvantages.map((adv) => (
              <div
                key={adv.id}
                className="group p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                  <i className={`${adv.icon} text-xl`}></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                  {t(adv.titleKey)}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t(adv.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section className="w-full py-16 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
              {t("membership.title")}
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              {t("membership.subtitle")}
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
            {membershipTiers.map((tier) => (
              <div
                key={tier.id}
                className={`rounded-xl p-5 border-2 ${tier.borderColor} bg-background-50 hover:scale-[1.02] transition-transform duration-300`}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-full ${tier.color} bg-opacity-20 mb-3`}>
                  <i className={`ri-vip-crown-fill text-lg ${tier.textColor}`}></i>
                </div>
                <h3 className={`font-heading text-base font-bold ${tier.textColor} mb-3`}>
                  {t(tier.nameKey)}
                </h3>
                <ul className="space-y-1.5">
                  {tier.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-foreground-600">
                      <i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promotions Section */}
      <section className="w-full py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between mb-12 gap-4">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-3">
                {t("promotions.title")}
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                {t("promotions.subtitle")}
              </h2>
            </div>
            <Link
              to="/category/promotions"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              {t("promotions.view_all")}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {homePromotions.map((promo) => (
              <Link
                key={promo.id}
                to={`/article/${promo.id === 1 ? 9 : promo.id === 2 ? 10 : 11}`}
                className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  {promo.id === 1 && (
                    <img
                      src="https://readdy.ai/api/search-image?query=Warm%20luxurious%20promotional%20scene%20with%20golden%20accents%20and%20soft%20ambient%20lighting%2C%20premium%20gift%20atmosphere%2C%20elegant%20abstract%20background%20with%20warm%20golden%20and%20crimson%20tones%2C%20clean%20composition%2C%20no%20text%20no%20people&width=800&height=480&seq=promo-new-member&orientation=landscape"
                      alt={promo.title}
                      title={promo.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  {promo.id === 2 && (
                    <img
                      src="https://readdy.ai/api/search-image?query=Warm%20luxurious%20promotional%20scene%20with%20golden%20accents%20and%20soft%20ambient%20lighting%2C%20premium%20gift%20atmosphere%2C%20elegant%20abstract%20background%20with%20warm%20golden%20and%20crimson%20tones%2C%20clean%20composition%2C%20no%20text%20no%20people&width=800&height=480&seq=promo-daily&orientation=landscape"
                      alt={promo.title}
                      title={promo.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  {promo.id === 3 && (
                    <img
                      src="https://readdy.ai/api/search-image?query=Warm%20luxurious%20promotional%20scene%20with%20golden%20accents%20and%20soft%20ambient%20lighting%2C%20premium%20gift%20atmosphere%2C%20elegant%20abstract%20background%20with%20warm%20golden%20and%20crimson%20tones%2C%20clean%20composition%2C%20no%20text%20no%20people&width=800&height=480&seq=promo-referral&orientation=landscape"
                      alt={promo.title}
                      title={promo.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  <div className="absolute top-3 left-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold text-background-50 ${promo.tagColor}`}>
                      {promo.tag}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {promo.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {promo.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative w-full py-20 md:py-24 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20luxurious%20warm%20gradient%20background%20with%20flowing%20golden%20and%20deep%20red%20waves%2C%20premium%20texture%2C%20soft%20glowing%20particles%2C%20dark%20elegant%20bottom%2C%20cinematic%20mood%2C%20no%20text%20no%20people&width=1800&height=600&seq=cta-bg-home&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/65"></div>
        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
            准备好开启您的精彩旅程了吗？
          </h2>
          <p className="text-base md:text-lg text-background-50/75 max-w-xl mx-auto mb-8">
            立即注册必赢国际，体验安全、公正、多元的国际顶级服务，享受专属新会员福利
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-background-50 rounded-lg text-base font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("hero.cta_register")}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </>
  );
}